import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { format, parseISO } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft, Brain, CheckCircle, XCircle, Clock, Loader2,
  FileText, Play, User, Mail, Briefcase, Star, AlertTriangle, Bot, ShieldCheck
} from "lucide-react";
import { cn } from "@/lib/utils";

const API = "/api";

const STATUS_CONFIG = {
  pending: { label: "Pending", color: "bg-slate-100 text-slate-700", icon: Clock },
  processing: { label: "Processing...", color: "bg-blue-100 text-blue-700", icon: Loader2 },
  pass: { label: "Pass", color: "bg-green-100 text-green-800", icon: CheckCircle },
  review: { label: "Review", color: "bg-amber-100 text-amber-800", icon: AlertTriangle },
  fail: { label: "Fail", color: "bg-red-100 text-red-800", icon: XCircle },
};

function ScoreBar({ label, score, feedback }) {
  const color = score >= 75 ? "bg-green-500" : score >= 60 ? "bg-amber-500" : "bg-red-500";
  const textColor = score >= 75 ? "text-green-700" : score >= 60 ? "text-amber-700" : "text-red-700";
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-slate-700">{label}</span>
        <span className={cn("text-sm font-bold", textColor)}>{score != null ? Math.round(score) : "-"}/100</span>
      </div>
      <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
        <div className={cn("h-full rounded-full transition-all duration-500", color)} style={{ width: `${score || 0}%` }} />
      </div>
      {feedback && <p className="text-xs text-slate-500 mt-0.5">{feedback}</p>}
    </div>
  );
}

export default function RecruitmentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const [applicant, setApplicant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [evaluating, setEvaluating] = useState(false);
  const [adminNotes, setAdminNotes] = useState("");
  const [decidingAs, setDecidingAs] = useState(null);

  const fetchApplicant = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/recruitment/applicants/${id}`, { headers: getAuthHeader() });
      setApplicant(res.data);
      setAdminNotes(res.data.admin_notes || "");
    } catch {
      toast.error("Failed to load applicant");
      navigate("/recruitment");
    } finally {
      setLoading(false);
    }
  }, [id, getAuthHeader, navigate]);

  useEffect(() => { fetchApplicant(); }, [fetchApplicant]);

  const handleEvaluate = async () => {
    setEvaluating(true);
    try {
      toast.info("AI is analyzing the video... This may take up to a minute.");
      const res = await axios.post(`${API}/recruitment/applicants/${id}/evaluate`, {}, { headers: getAuthHeader(), timeout: 180000 });
      toast.success(`Evaluation complete! Score: ${res.data.overall_score} — AI Decision: ${(res.data.ai_decision || "review").toUpperCase()}`);
      fetchApplicant();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Evaluation failed");
    } finally {
      setEvaluating(false);
    }
  };

  const handleDecision = async (decision) => {
    setDecidingAs(decision);
    try {
      const fd = new FormData();
      fd.append("decision", decision);
      fd.append("notes", adminNotes);
      await axios.put(`${API}/recruitment/applicants/${id}/decision`, fd, { headers: getAuthHeader() });
      toast.success(`Decision overridden to: ${decision.toUpperCase()}`);
      fetchApplicant();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to update decision");
    } finally {
      setDecidingAs(null);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading...</div>;
  }
  if (!applicant) return null;

  const sc = STATUS_CONFIG[applicant.status] || STATUS_CONFIG.pending;
  const evaluation = applicant.ai_evaluation || {};
  const videoUrl = applicant.video_path ? `${API}/recruitment/files/${applicant.video_path}` : null;
  const resumeUrl = applicant.resume_path ? `${API}/recruitment/files/${applicant.resume_path}` : null;
  const aiDecision = applicant.ai_decision;
  const adminDecision = applicant.admin_decision;
  const isOverridden = aiDecision && adminDecision && aiDecision !== adminDecision;

  return (
    <div className="space-y-6" data-testid="recruitment-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate("/recruitment")} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900" data-testid="applicant-name">{applicant.name}</h1>
          <div className="flex items-center gap-4 text-sm text-slate-500 mt-1">
            <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {applicant.email}</span>
            <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {format(parseISO(applicant.created_at), "MMM dd, yyyy 'at' HH:mm")}</span>
          </div>
        </div>
        <Badge className={cn("text-sm px-3 py-1", sc.color)} data-testid="applicant-status">{sc.label}</Badge>
        {adminDecision && (
          <Badge className={cn("text-sm px-3 py-1", adminDecision === "hired" ? "bg-green-600 text-white" : "bg-red-600 text-white")} data-testid="applicant-decision">
            {adminDecision.toUpperCase()}
          </Badge>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Video Player */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><Play className="w-4 h-4" /> Video Introduction</CardTitle>
            </CardHeader>
            <CardContent>
              {videoUrl ? (
                <div className="rounded-lg overflow-hidden bg-black">
                  <video controls className="w-full max-h-[420px]" src={videoUrl} data-testid="applicant-video">
                    Your browser does not support video playback.
                  </video>
                </div>
              ) : (
                <div className="text-center py-12 text-slate-400">No video available</div>
              )}
              {applicant.video_duration && (
                <p className="text-xs text-slate-500 mt-2">Duration: {Math.floor(applicant.video_duration / 60)}:{String(Math.round(applicant.video_duration % 60)).padStart(2, "0")}</p>
              )}
            </CardContent>
          </Card>

          {/* Transcript */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><FileText className="w-4 h-4" /> Transcript</CardTitle>
            </CardHeader>
            <CardContent>
              {applicant.transcript ? (
                <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-700 leading-relaxed whitespace-pre-wrap max-h-80 overflow-y-auto" data-testid="applicant-transcript">
                  {applicant.transcript}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-400">
                  {applicant.status === "pending" ? "Run AI evaluation to generate transcript" : "No transcript available"}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Experience */}
          {applicant.experience && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2"><Briefcase className="w-4 h-4" /> Teaching Experience</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-700 whitespace-pre-wrap" data-testid="applicant-experience">{applicant.experience}</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Re-evaluate Button (only for pending/failed) */}
          {(applicant.status === "pending" || applicant.status === "fail") && (
            <Button onClick={handleEvaluate} disabled={evaluating} variant="outline" className="w-full" data-testid="evaluate-btn">
              {evaluating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Evaluating...</> : <><Brain className="w-4 h-4 mr-2" /> {applicant.status === "pending" ? "Run AI Evaluation" : "Re-evaluate"}</>}
            </Button>
          )}
          {applicant.status === "processing" && (
            <div className="w-full text-center py-3 bg-blue-50 rounded-lg">
              <Loader2 className="w-5 h-5 animate-spin mx-auto text-blue-600 mb-1" />
              <p className="text-sm text-blue-700 font-medium">AI is evaluating...</p>
              <p className="text-xs text-blue-500">This may take up to a minute</p>
            </div>
          )}

          {/* Overall Score */}
          {applicant.overall_score != null && (
            <Card className={cn("border-2", applicant.overall_score >= 75 ? "border-green-200" : applicant.overall_score >= 60 ? "border-amber-200" : "border-red-200")}>
              <CardContent className="pt-6 pb-6 text-center">
                <div className={cn("text-5xl font-black", applicant.overall_score >= 75 ? "text-green-600" : applicant.overall_score >= 60 ? "text-amber-600" : "text-red-600")} data-testid="overall-score">
                  {Math.round(applicant.overall_score)}
                </div>
                <p className="text-sm text-slate-500 mt-1">Overall Score</p>
                <Badge className={cn("mt-2", sc.color)}>{sc.label}</Badge>
              </CardContent>
            </Card>
          )}

          {/* AI Decision Card */}
          {(aiDecision || (applicant.status && applicant.status !== "pending" && applicant.status !== "processing")) && (
            <Card className={cn("border-2", aiDecision === "hired" ? "border-green-300 bg-green-50/50" : aiDecision === "rejected" ? "border-red-300 bg-red-50/50" : "border-amber-300 bg-amber-50/50")}>
              <CardContent className="pt-5 pb-5">
                <div className="flex items-center gap-2 mb-3">
                  <Bot className="w-5 h-5 text-indigo-600" />
                  <span className="font-semibold text-sm text-slate-800">AI Decision</span>
                </div>
                {aiDecision ? (
                  <div className="text-center">
                    <Badge className={cn("text-base px-4 py-1.5 font-bold", aiDecision === "hired" ? "bg-green-600 text-white" : "bg-red-600 text-white")} data-testid="ai-decision-badge">
                      {aiDecision === "hired" ? <><CheckCircle className="w-4 h-4 mr-1.5" />HIRED</> : <><XCircle className="w-4 h-4 mr-1.5" />REJECTED</>}
                    </Badge>
                    <p className="text-xs text-slate-500 mt-2">
                      {aiDecision === "hired" ? "Score >= 75: AI recommends hiring" : "Score < 60: AI recommends rejection"}
                    </p>
                  </div>
                ) : (
                  <div className="text-center">
                    <Badge className="text-base px-4 py-1.5 font-bold bg-amber-500 text-white" data-testid="ai-decision-badge">
                      <AlertTriangle className="w-4 h-4 mr-1.5" />NEEDS REVIEW
                    </Badge>
                    <p className="text-xs text-slate-500 mt-2">Score 60-74: AI defers to admin review</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Detailed Scores */}
          {applicant.grammar_score != null && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2"><Star className="w-4 h-4" /> AI Evaluation Scores</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <ScoreBar label="Grammar" score={applicant.grammar_score} feedback={evaluation.grammar_feedback} />
                <ScoreBar label="Pronunciation" score={applicant.pronunciation_score} feedback={evaluation.pronunciation_feedback} />
                <ScoreBar label="Fluency" score={applicant.fluency_score} feedback={evaluation.fluency_feedback} />
                <ScoreBar label="Vocabulary" score={applicant.vocabulary_score} feedback={evaluation.vocabulary_feedback} />
                <ScoreBar label="Teaching Ability" score={applicant.teaching_score} feedback={evaluation.teaching_feedback} />
              </CardContent>
            </Card>
          )}

          {/* Overall Feedback */}
          {evaluation.overall_feedback && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Overall Assessment</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-slate-700" data-testid="overall-feedback">{evaluation.overall_feedback}</p>
                {evaluation.strengths?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-green-700 mb-1">Strengths</p>
                    <ul className="text-xs text-slate-600 space-y-0.5">
                      {evaluation.strengths.map((s, i) => <li key={i} className="flex items-start gap-1"><CheckCircle className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" /> {s}</li>)}
                    </ul>
                  </div>
                )}
                {evaluation.improvements?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-amber-700 mb-1">Areas for Improvement</p>
                    <ul className="text-xs text-slate-600 space-y-0.5">
                      {evaluation.improvements.map((s, i) => <li key={i} className="flex items-start gap-1"><AlertTriangle className="w-3 h-3 text-amber-500 mt-0.5 flex-shrink-0" /> {s}</li>)}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Resume Link */}
          {resumeUrl && (
            <a href={resumeUrl} target="_blank" rel="noopener noreferrer" data-testid="resume-link">
              <Button variant="outline" className="w-full">
                <FileText className="w-4 h-4 mr-2" /> View Resume
              </Button>
            </a>
          )}

          {/* Admin Override */}
          {applicant.status !== "pending" && applicant.status !== "processing" && (
            <Card className={cn(isOverridden ? "border-2 border-indigo-300" : "")}>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4" /> Admin Override
                  {isOverridden && <Badge className="bg-indigo-100 text-indigo-700 text-xs ml-1">Overridden</Badge>}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {isOverridden && (
                  <div className="bg-indigo-50 rounded-lg p-3 text-xs text-indigo-700">
                    AI recommended <strong>{aiDecision?.toUpperCase()}</strong>, but you changed it to <strong>{adminDecision?.toUpperCase()}</strong>.
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="admin-notes">Notes</Label>
                  <Textarea
                    id="admin-notes"
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    placeholder="Add notes about this applicant..."
                    rows={3}
                    data-testid="admin-notes"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    onClick={() => handleDecision("hired")}
                    disabled={!!decidingAs}
                    className={cn("text-white", adminDecision === "hired" ? "bg-green-700 hover:bg-green-800" : "bg-green-600 hover:bg-green-700")}
                    data-testid="hire-btn"
                  >
                    {decidingAs === "hired" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle className="w-4 h-4 mr-1" /> {adminDecision === "hired" ? "Hired" : "Override: Hire"}</>}
                  </Button>
                  <Button
                    onClick={() => handleDecision("rejected")}
                    disabled={!!decidingAs}
                    variant="outline"
                    className={cn(adminDecision === "rejected" ? "bg-red-100 border-red-400 text-red-700" : "border-red-300 text-red-600 hover:bg-red-50")}
                    data-testid="reject-btn"
                  >
                    {decidingAs === "rejected" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><XCircle className="w-4 h-4 mr-1" /> {adminDecision === "rejected" ? "Rejected" : "Override: Reject"}</>}
                  </Button>
                </div>
                {adminDecision && (
                  <p className="text-xs text-slate-500 text-center">
                    Current final decision: <span className="font-semibold">{adminDecision.toUpperCase()}</span>
                    {aiDecision && adminDecision === aiDecision && <span className="text-green-600 ml-1">(matches AI)</span>}
                  </p>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
